/**
 * SimEngine: the deterministic core every lane's sim code plugs into.
 * Owns clock, RNG streams, command bus, systems. Pure TS - no DOM, no timers,
 * no Date.now, no Math.random, no transcendental Math (contract: sim.ts).
 * Real-time pacing lives outside (src/worker); the engine only steps ticks.
 */
import type { SimCommand, SimSnapshot, Speed, Tick } from '../../contracts/sim';
import { Clock } from './clock';
import { CommandBus, type CommandHandler } from './bus';
import { RngRegistry, type RngInternalState } from './rng';
import { BinWriter, BinReader } from './serialize';
import { hashBytes } from './hash';
import { encodeEntityBuffer } from './snapshot-sections';

export const STATE_MAGIC = 'EAS1';
/**
 * v0: system blobs inline, restore order = serialize order (fragile across
 *     intra-system format changes).
 * v1: each system blob is length-prefixed, so restore reads exactly the
 *     bytes a system wrote; intra-system versioning (e.g. mgmt) is then a
 *     per-system concern, not an engine-envelope one. v0 saves still load
 *     via the legacy branch in loadInto.
 */
export const STATE_VERSION = 2;
/**
 * Sim level of detail (P616): per-system LOD tier multipliers applied on
 * top of the system's static tickCadence (P118). Tiers change ONLY through
 * the 'sim.setLod' bus command, so replays of the same command log draw
 * identically; tiers serialize with engine state. 1 tick = 1 in-game hour:
 * reduced = every 4h, background = daily.
 */
export const LOD_MULTIPLIERS = [1, 4, 24] as const;
export type LodTier = 0 | 1 | 2; // 0 full, 1 reduced, 2 background

/** A pluggable deterministic system (Lane 4+ registers the real ones). */
export interface SimSystem<TState = unknown> {
  name: string;
  /** Create the initial state for a fresh scenario. */
  init(ctx: SystemContext): TState;
  /** Advance one tick. Use ctx.rng(stream) - never Math.random. */
  onTick(ctx: SystemContext, state: TState, tick: Tick): void;
  /**
   * Multi-rate scheduling (P118): when set to N > 1, onTick runs only on
   * ticks divisible by N. Deterministic (tick-indexed, not wall-time). A
   * system opting in owns scaling its per-tick rates by N.
   */
  tickCadence?: number;
  /** Authoritative hourly obligations may temporarily override LOD without
   * changing the requested tier. Evaluated from saved state before each tick. */
  requiresFullRate?(state: TState, tick: Tick): boolean;
  /** Canonical binary serialization (writer order is the canonical order). */
  serialize(w: BinWriter, state: TState): void;
  deserialize(r: BinReader): TState;
  /** Additive system omitted by an older save supplies clean defaults on replacement. */
  missingFromSave?(): TState;
  /** Optional: per-frame draw poses for the renderer (contracts/render.ts). */
  collectRenderPoses?(state: TState): import('../../contracts/render').RenderPose[];
  /** Optional: field-mode slice (contracts/field.ts). */
  collectFieldSlice?(state: TState): import('../../contracts/field').FieldSlice | null;
  /** Optional: UI-facing state projection (snapshot 'UIST' section). */
  collectUiState?(state: TState): unknown;
}

export interface SystemContext {
  rng(stream: string): import('./rng').Rng;
  /** Emit a system command scheduled at a future tick. */
  emit(type: string, payload: unknown, atTick: Tick): void;
  readonly seed: number;
}

interface RegisteredSystem {
  system: SimSystem;
  state: unknown;
}

function parseCommandSource(s: string): SimCommand['source'] {
  if (s === 'player' || s === 'system' || s === 'bot') return s;
  throw new Error(`state: bad command source '${s}'`);
}

export class SimEngine {
  readonly clock = new Clock();
  readonly rng: RngRegistry;
  readonly bus: CommandBus;
  /**
   * Optional sink for failures while EXECUTING a queued command (set by
   * content registration). ack only means "validated and queued"; execution
   * happens here at step time, and a state-dependent refusal (a CommandError
   * from a handler) must not kill the tick loop or silently rewind to the
   * autosave - the sink turns it into visible feedback. Rethrow from inside
   * the sink to keep halt behavior for genuine bugs.
   */
  onCommandError?: (command: SimCommand, err: unknown) => void;
  private systems = new Map<string, RegisteredSystem>();
  /** P616: dynamic LOD tier per system (absent = full). In state: v2+. */
  private lodTiers = new Map<string, LodTier>();
  readonly seed: number;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    this.rng = new RngRegistry(this.seed);
    this.bus = new CommandBus(this.rng);
    this.registerLodCommand();
  }

  registerSystem(system: SimSystem): void {
    if (this.systems.has(system.name)) {
      throw new Error(`engine: duplicate system '${system.name}'`);
    }
    if (system.tickCadence !== undefined && (!Number.isInteger(system.tickCadence) || system.tickCadence < 1)) {
      throw new Error(`engine: system '${system.name}' tickCadence must be an integer >= 1`);
    }
    const ctx = this.systemContext();
    this.systems.set(system.name, { system, state: system.init(ctx) });
  }

  registerCommand(handler: CommandHandler): void {
    this.bus.register(handler);
  }

  private systemContext(): SystemContext {
    return {
      rng: (stream) => this.rng.get(stream),
      emit: (type, payload, atTick) => {
        const res = this.bus.dispatch({
          type, payload, source: 'system', tick: atTick, currentTick: this.clock.tick,
        });
        if (!res.ok) throw new Error(`engine: system emit rejected - ${res.reason}`);
      },
      seed: this.seed,
    };
  }

  /** System state, typed at the call site (cast required - engine is generic). */
  getSystemState(name: string): unknown {
    const s = this.systems.get(name);
    if (!s) throw new Error(`engine: no system '${name}'`);
    return s.state;
  }

  /** Player/bot intents enter here. Rejects unknown types at the boundary. */
  dispatch(input: { type: string; payload: unknown; source: SimCommand['source']; tick?: Tick }) {
    const res = this.bus.dispatch({
      type: input.type,
      payload: input.payload,
      source: input.source,
      tick: input.tick ?? this.clock.tick,
      currentTick: this.clock.tick,
    });
    // Command-processing contract (2026-10-22 live divergence): a command due
    // at the CURRENT tick executes on arrival - commands-before-systems order
    // and per-tick rng interleave are identical to executing at the next step,
    // so determinism is preserved and no clock time advances. Without this,
    // scenario.start sat in the queue up to 90s of wall time (fresh Begin
    // showed $0 until the first tick) and paused clicks looked dropped.
    // Future-tick commands stay queued for step() exactly as before.
    if (res.ok) this.executeDue(this.clock.tick);
    return res;
  }

  /** Execute every queued command due at `tick`, in deterministic order. */
  private executeDue(tick: Tick): void {
    const due = this.bus.takeDue(tick);
    for (const command of due) {
      const ctx = { tick, engine: this, commandId: command.id };
      try {
        this.bus.execute(ctx, command);
      } catch (err) {
        // No sink registered: preserve the halt (P029) so bugs stay loud.
        if (this.onCommandError === undefined) throw err;
        this.onCommandError(command, err);
      }
    }
  }

  /** Step exactly one tick: due commands first (deterministic order), then systems. */
  step(): void {
    const nextTick = this.clock.tick + 1;
    this.clock.stepTicks(1);
    this.executeDue(nextTick);
    const sysCtx = this.systemContext();
    for (const { system, state } of this.sortedSystems()) {
      const cadence = system.requiresFullRate?.(state, nextTick) ? 1 : (system.tickCadence ?? 1) * LOD_MULTIPLIERS[this.lodTiers.get(system.name) ?? 0];
      if (cadence > 1 && nextTick % cadence !== 0) continue;
      system.onTick(sysCtx, state, nextTick);
    }
  }

  stepMany(n: number): void {
    for (let i = 0; i < n; i++) this.step();
  }

  setSpeed(speed: Speed): void { this.clock.setSpeed(speed); }

  /**
   * P616: set a system's LOD tier. Unknown system names are a silent no-op
   * (stale director command; mirrors field.track's stale-id rule). Tier 0
   * restores full rate. Prefer dispatching 'sim.setLod' so the change is
   * replayed from the command log.
   */
  private registerLodCommand(): void {
    this.registerCommand({
      type: 'sim.setLod',
      validate(p) {
        if (typeof p !== 'object' || p === null) return 'payload must be an object';
        const r = p as Record<string, unknown>;
        if (typeof r.system !== 'string') return 'system (string) required';
        if (!Number.isInteger(r.tier) || (r.tier as number) < 0 || (r.tier as number) > 2) return 'tier must be 0, 1 or 2';
        return null;
      },
      execute: (ctx, payload) => {
        const p = payload as { system: string; tier: LodTier };
        (ctx.engine as SimEngine).setSystemLod(p.system, p.tier);
      },
    });
  }

  setSystemLod(name: string, tier: LodTier): void {
    if (!this.systems.has(name)) return;
    if (tier === 0) this.lodTiers.delete(name);
    else this.lodTiers.set(name, tier);
  }

  systemLod(name: string): LodTier { return this.lodTiers.get(name) ?? 0; }

  /** Systems serialize in sorted-name order: canonical, registration-independent. */
  private sortedSystems(): RegisteredSystem[] {
    return [...this.systems.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([, v]) => v);
  }

  /** Canonical full-state bytes. Byte-identical across engines for same state. */
  serializeState(): Uint8Array {
    const w = new BinWriter();
    w.str(STATE_MAGIC).u32(STATE_VERSION);
    w.u32(this.seed);
    w.u32(this.clock.tick).u8(this.clock.speed);
    // RNG streams (sorted names inside RngRegistry.serialize()).
    const streams: RngInternalState[] = this.rng.serialize();
    w.u32(streams.length);
    for (const s of streams) {
      w.str(s.stream).u32(s.seed).u32(s.a).u32(s.b).u32(s.c).u32(s.d);
    }
    // Pending commands (already in deterministic order).
    const pending = this.bus.pending();
    w.u32(pending.length);
    for (const c of pending) this.writeCommand(w, c);
    // LOD tiers (v2+), sorted by system name: canonical.
    const tiers = [...this.lodTiers.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    w.u32(tiers.length);
    for (const [name, tier] of tiers) w.str(name).u8(tier);
    // Systems, sorted by name. Each blob is length-prefixed (v1).
    const systems = this.sortedSystems();
    w.u32(systems.length);
    for (const { system, state } of systems) {
      const sub = new BinWriter();
      system.serialize(sub, state);
      w.str(system.name);
      w.bytes(sub.finish());
    }
    return w.finish();
  }

  private writeCommand(w: BinWriter, c: SimCommand): void {
    w.str(c.id).u32(c.tick).str(c.type).str(c.source);
    const p = new BinWriter();
    writePayload(p, c.payload);
    w.bytes(p.finish());
  }

  /** Deterministic state hash - the cross-engine identity check. */
  stateHash(): string {
    return hashBytes(this.serializeState());
  }

  /**
   * Binary snapshot for renderer/UI. They read; they never own state.
   * entities is the section container (contracts/render.ts): 'FILD' + 'POSE'
   * sections filled by system hooks, empty until content lanes wire them.
   */
  snapshot(): SimSnapshot {
    const poses: import('../../contracts/render').RenderPose[] = [];
    let field: import('../../contracts/field').FieldSlice | null = null;
    const ui: Record<string, unknown> = {};
    for (const { system, state } of this.sortedSystems()) {
      if (system.collectRenderPoses) poses.push(...system.collectRenderPoses(state));
      if (system.collectFieldSlice) {
        const slice = system.collectFieldSlice(state);
        if (slice) field = slice;
      }
      if (system.collectUiState) {
        const u = system.collectUiState(state);
        if (u !== undefined && u !== null) ui[system.name] = u;
      }
    }
    // Telemetry (P0B12): bus command-flow counters, diagnostics only -
    // never serialized into state, so the hash is unaffected.
    ui['telemetry'] = this.bus.stats();
    return {
      version: 0,
      tick: this.clock.tick,
      hash: this.stateHash(),
      entities: encodeEntityBuffer({ poses, field, ui }),
    };
  }

  /**
   * Load canonical bytes into an engine that already has the same systems
   * and command handlers registered. This is the save/load path.
   */
  loadInto(data: Uint8Array): void {
    const r = new BinReader(data);
    const magic = r.str();
    if (magic !== STATE_MAGIC) throw new Error(`state: bad magic '${magic}'`);
    const version = r.u32();
    if (version > STATE_VERSION) throw new Error(`state: unsupported version ${String(version)}`);
    const seed = r.u32();
    if (seed !== this.seed) throw new Error(`state: seed mismatch (save ${String(seed)}, engine ${String(this.seed)})`);
    const nextTick = r.u32(), nextSpeed = r.u8() as Speed;
    // Validate before touching even the sub-tick pacing of the current clock.
    const checkedClock = new Clock(); checkedClock.restore(nextTick, nextSpeed);
    const streamCount = r.u32();
    const streams: RngInternalState[] = [];
    for (let i = 0; i < streamCount; i++) {
      streams.push({ stream: r.str(), seed: r.u32(), a: r.u32(), b: r.u32(), c: r.u32(), d: r.u32() });
    }
    const checkedRng = new RngRegistry(this.seed); checkedRng.restore(streams);
    const pendingCount = r.u32();
    const pending: SimCommand[] = [];
    for (let i = 0; i < pendingCount; i++) {
      const id = r.str(); const tick = r.u32(); const type = r.str();
      const source = parseCommandSource(r.str());
      const payload = readPayload(new BinReader(r.bytes()));
      pending.push({ id, tick, type, source, payload });
    }
    const nextTiers = new Map<string, LodTier>();
    if (version >= 2) {
      const tierCount = r.u32();
      for (let i = 0; i < tierCount; i++) {
        const name = r.str();
        const tier = r.u8();
        if ((tier === 1 || tier === 2) && this.systems.has(name)) nextTiers.set(name, tier);
      }
    }
    // v0/v1 saves carry no tiers: every system loads at full rate.
    const nextStates = new Map<string, unknown>();
    const systemCount = r.u32();
    for (let i = 0; i < systemCount; i++) {
      const name = r.str();
      const reg = this.systems.get(name);
      if (!reg) throw new Error(`state: save contains system '${name}' not registered in this build`);
      if (version === 0) {
        // Legacy: inline blob, exact positional read.
        nextStates.set(name, reg.system.deserialize(r));
      } else {
        // v1: length-prefixed blob. The system owns its byte range; bytes it
        // does not read (older intra-system versions) stay inside the range.
        const blob = r.bytes();
        nextStates.set(name, reg.system.deserialize(new BinReader(blob)));
      }
    }
    if (!r.done) throw new Error('state: trailing bytes after load');
    // Deserializers are pure state decoders. Only after the entire payload
    // succeeds do we replace live state. No save/storage/offline policy here.
    this.clock.restore(nextTick, nextSpeed);
    this.rng.restore(streams);
    this.bus.restorePending(pending);
    this.lodTiers = nextTiers;
    for (const [name, reg] of this.systems) {
      if (!nextStates.has(name) && reg.system.missingFromSave) nextStates.set(name, reg.system.missingFromSave());
    }
    for (const [name, state] of nextStates) {
      const reg = this.systems.get(name);
      if (reg) reg.state = state;
    }
  }
}

/** Canonical payload encoding: JSON value tree with sorted object keys. */
export function writePayload(w: BinWriter, v: unknown): void {
  if (v === null) { w.u8(0); return; }
  if (typeof v === 'boolean') { w.u8(1); w.bool(v); return; }
  if (typeof v === 'number') { w.u8(2); w.f64(v); return; }
  if (typeof v === 'string') { w.u8(3); w.str(v); return; }
  if (Array.isArray(v)) {
    w.u8(4); w.u32(v.length);
    for (const item of v) writePayload(w, item);
    return;
  }
  if (typeof v === 'object') {
    w.u8(5);
    const keys = Object.keys(v).sort();
    w.u32(keys.length);
    for (const k of keys) {
      w.str(k);
      writePayload(w, (v as Record<string, unknown>)[k]);
    }
    return;
  }
  throw new Error(`payload: unencodable value of type ${typeof v}`);
}

export function readPayload(r: BinReader): unknown {
  const tag = r.u8();
  switch (tag) {
    case 0: return null;
    case 1: return r.bool();
    case 2: return r.f64();
    case 3: return r.str();
    case 4: {
      const len = r.u32();
      const out: unknown[] = [];
      for (let i = 0; i < len; i++) out.push(readPayload(r));
      return out;
    }
    case 5: {
      const len = r.u32();
      const out: Record<string, unknown> = {};
      for (let i = 0; i < len; i++) out[r.str()] = readPayload(r);
      return out;
    }
    default: throw new Error(`payload: bad tag ${String(tag)}`);
  }
}
